import { makeStyles } from "@material-ui/core/styles";
import { useParams } from "react-router-dom";
import {
  Box,
  Drawer,
  Typography,
  Fab,
  Tooltip,
  useTheme,
  useMediaQuery,
  Paper,
  Grid,
  Button,
  Switch,
} from "@material-ui/core";
import { i18n } from "../../translate/i18n";
import classNames from "classnames";
import TicketManagementFilters from "../../components/TicketManagement/filters";
import TicketManagementList from "../../components/TicketManagement/list";
import Ticket from "../../components/Ticket";
import { useEffect, useState, useReducer, useContext, useRef } from "react";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import FilterListOutlinedIcon from "@material-ui/icons/FilterListOutlined";
import { AuthContext } from "../../context/Auth/AuthContext";
import socket from "../../hooks/useSocket";
import Logo from "../../assets/logo_login.png";
import Chart from "react-apexcharts";
import { colors } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    maxWidth: "100vw",
    margin: "20px 10px!important",
  },
  title: {
    fontSize: "1.3rem",
    fontWeight: "800",
    color: "#616161",
  },
  content: {
    display: "flex",
    height: "calc(100vh - 105px)",
  },
  baseContent: {
    padding: "1rem",
    boxSizing: "border-box",
  },
  filterColumn: {
    overflowY: "auto",
    flex: " 1 1 17%",
    minWidth: "200px",
    maxWidth: "300px",
    marginRight: "10px",
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },
  listColumn: {
    flex: "1 1 30%",
    minWidth: "250px",
    maxWidth: "400px",
    display: "block",
    position: "relative",
    overflowY: "auto",
    marginRight: "10px",
    backgroundColor: theme.palette.background.default,
    paddingTop: 0,
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
      maxWidth: "100%",
      width: "100%",
    },
  },
  detailColumn: {
    flex: "2 1 50%",
    backgroundColor: theme.palette.background.default,
    display: "block",
  },
  filterMobileButton: {
    bottom: "10px",
    position: "absolute",
    right: "10px",
    display: "none",
    [theme.breakpoints.down("md")]: {
      display: "block",
    },
  },
  filterMobile: {
    width: 300,
    padding: 10,
  },
  welcomeMsg: {
    paddingTop: "22%",
    backgroundColor: "transparent",
    boxShadow: "none",
    border: "none",
    justifyContent: "space-evenly",
    alignItems: "center",
    height: "100%",
    textAlign: "center",
    borderRadius: 0,
  },
  button: {
    padding: "7px",
    float: "right",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
    },
    "& svg": {
      color: theme.palette.primary.contrastText,
      marginRight: "5px",
    },
  },
}));

// ORDENAR OS TICKETS
const orderTickets = (tckts) => {
  if (!tckts || !Array.isArray(tckts) || tckts.length == 0) return [];

  const statusOrder = { open: 0, pending: 1, closed: 2 };

  return tckts.slice().sort((a, b) => {
    const statusA = statusOrder[a.status] ?? 99;
    const statusB = statusOrder[b.status] ?? 99;

    if (statusA !== statusB) {
      return statusA - statusB;
    }

    const dateA = a.lastMessageDate ? new Date(a.lastMessageDate).getTime() : 0;
    const dateB = b.lastMessageDate ? new Date(b.lastMessageDate).getTime() : 0;

    return dateB - dateA; // Mais recente primeiro
  });
};

const reducer = (state, action) => {
  if (action.type === "LOAD_TICKETS") {
    const newTickets = action.payload;
    return orderTickets([...newTickets]);
  }

  if (action.type === "RESET_UNREAD") {
    const { ticketId, lastMessage } = action.payload || {};
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
      if (lastMessage) state[ticketIndex].lastMessage = lastMessage;
    }
    return orderTickets([...state]);
  }

  if (action.type === "UPDATE_TICKET") {
    const ticket = action.payload;
    const ticketIndex = state.findIndex(
      (t) => t.id == ticket.id && t.uuid == ticket.uuid
    );

    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
    } else {
      state.unshift(ticket);
    }

    return orderTickets([...state]);
  }

  if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
    const ticket = action.payload;
    if (ticket?.queueId) {
      const ticketIndex = state.findIndex(
        (t) => t.id == ticket.id && t.uuid == ticket.uuid
      );
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        state.unshift(...state.splice(ticketIndex, 1));
      } else {
        state.unshift(ticket);
      }
    }
    return orderTickets([...state]);
  }

  if (action.type === "UPDATE_TICKET_CONTACT") {
    const contact = action.payload;
    if (contact) {
      const ticketIndex = state.findIndex((t) => t.contactId === contact?.id);
      if (ticketIndex !== -1) {
        state[ticketIndex].Contact = contact;
      }
    }
    return orderTickets([...state]);
  }

  if (action.type === "DELETE_TICKET") {
    const ticketId = action.payload;
    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) state.splice(ticketIndex, 1);
    return orderTickets([...state]);
  }

  if (action.type === "RESET") {
    return [];
  }

  // Retorne o estado original se nenhuma ação corresponder
  return state;
};

const RankingAI = () => {
  const classes = useStyles();
  var heightChartDefault = "100%";

  const [open, setOpen] = useState(false);
  const { ticketId, contactId } = useParams();
  const { user } = useContext(AuthContext);

  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [tickets, dispatch] = useReducer(reducer, []);
  const [totalCount, setTotalCount] = useState(0);
  const [contacts, setContacts] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [searchDate, setSearchDate] = useState({ start: "", end: "" });
  const [originDate, setOriginDate] = useState("createdAt");
  var initialContent = { start: "", end: "", status: "" };
  const [values, setValues] = useState(initialContent);

  const [selectedsConnectionsAndQueues, setSelectedsConnectionsAndQueues] =
    useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const userStatus = [
    { key: "loggedIn", value: i18n.t("ticketManagement.filters.loggedIn") },
    { key: "loggedOut", value: i18n.t("ticketManagement.filters.loggedOut") },
  ];
  const [selectedAssigned, setSelectedAssigned] = useState([]);
  const assigneds = [
    { key: "assigned", value: i18n.t("ticketManagement.filters.assigned") },
    { key: "unassigned", value: i18n.t("ticketManagement.filters.unassigned") },
  ];
  const [selectedTicketStatus, setSelectedTicketStatus] = useState([]);
  const ticketStatus = [
    { key: "open", value: i18n.t("ticketManagement.filters.open") },
    { key: "pending", value: i18n.t("ticketManagement.filters.pending") },
    { key: "closed", value: i18n.t("ticketManagement.filters.closed") },
  ];
  var defaultResolution = {
    filter: "col-xl-2",
    donuts: "col-xl-4",
    table: "col-xl-6",
  };
  const [bootstrapResolution, setBootstrapResolution] =
    useState(defaultResolution);
  const [heightChart, setHeightChart] = useState(heightChartDefault);
  const [dateTime, setDateTime] = useState({ start: "", end: "" });
  // Adicione este estado após os outros estados
  const [chartData, setChartData] = useState({
    options: {
      chart: {
        id: "top-10-atendimentos",
        toolbar: {
          show: false,
        },
        background: "transparent", // Fundo transparente
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "45%",
          distributed: false,
          dataLabels: {
            position: "top",
          },
        },
      },
      colors: ["#0B708C"], // Cor específica que você solicitou
      dataLabels: {
        enabled: true,
        formatter: function (val) {
          return val;
        },
        offsetY: -20,
        style: {
          fontSize: "12px",
          colors: ["#333"],
          fontWeight: "bold",
        },
      },
      xaxis: {
        categories: [
          "Categoria 1",
          "Categoria 2",
          "Categoria 3",
          "Categoria 4",
          "Categoria 5",
          "Categoria 6",
          "Categoria 7",
          "Categoria 8",
          "Categoria 9",
          "Categoria 10",
        ],
        labels: {
          style: {
            fontSize: "12px",
            fontWeight: "bold",
          },
          rotate: -45,
        },
        axisBorder: {
          show: true,
          color: "#e0e0e0",
        },
        axisTicks: {
          show: true,
          color: "#e0e0e0",
        },
      },
      yaxis: {
        title: {
          text: "Quantidade",
          style: {
            fontSize: "12px",
            fontWeight: "bold",
            color: "#333",
          },
        },
        labels: {
          style: {
            colors: "#333",
          },
        },
        axisBorder: {
          show: true,
          color: "#e0e0e0",
        },
        axisTicks: {
          show: true,
          color: "#e0e0e0",
        },
      },
      title: {
        text: "Ranking Top 10 - Atendimentos",
        align: "center",
        style: {
          fontSize: "16px",
          fontWeight: "bold",
          color: "#333",
        },
      },
      tooltip: {
        theme: "light",
        y: {
          formatter: function (val) {
            return val;
          },
        },
      },
      grid: {
        show: false, // Remove completamente a grade de fundo
        padding: {
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        },
      },
      stroke: {
        show: true,
        width: 1,
        colors: ["#fff"], // Borda branca nas barras
      },
    },
    series: [
      {
        name: "Atendimentos",
        data: [100, 80, 60, 30, 300, 200, 100, 50, 15, 10],
      },
    ],
  });

  // Função para calcular o primeiro e o último dia do mês atual
  const getCurrentMonthDates = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Último dia do mês
    return {
      start: start.toISOString().split("T")[0],
      end: end.toISOString().split("T")[0],
    };
  };

  useEffect(() => {
    const { start, end } = getCurrentMonthDates();
    setDateTime({ start, end });
    setSearchDate({ start, end });
  }, []);
  const handleSocketEvents = (data) => {
    let ticket = data.ticket;
    if (!ticket && data?.Ticket) ticket = data.Ticket;
    if (!ticket && data?.message?.Ticket) ticket = data.message.Ticket;

    switch (data.action) {
      case "updateUnread":
        dispatch({
          type: "RESET_UNREAD",
          payload: {
            ticketId: data.ticketId,
            lastMessage: data.ticket.lastMessage,
          },
        });
        break;
      case "update":
        // verificar filtro, se passar atualizar, se não deletar
        if (shouldUpdateTicket(ticket) == true)
          dispatch({ type: "UPDATE_TICKET", payload: ticket });
        else dispatch({ type: "DELETE_TICKET", payload: ticket.id });
        break;
      case "delete":
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
        break;
      case "create":
        if (shouldUpdateTicket(ticket))
          dispatch({ type: "UPDATE_TICKET_UNREAD_MESSAGES", payload: ticket });
        break;
      case "updateContact":
        dispatch({ type: "UPDATE_TICKET_CONTACT", payload: data.contact });
        break;

      default:
        break;
    }
  };

  const formatDate1Week = () => {
    var uma_semana = new Date();
    uma_semana.setDate(uma_semana.getDate() - 8);
    return new Date(uma_semana).toISOString().split("T")[0];
  };

  // Adicione este useEffect para buscar dados reais
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchData = async () => {
      try {
        let params = { searchDate, originDate };
        const { data } = await api.get("/categoriesRanking", {
          params,
          signal: abortController.signal,
        });
        if (!isMounted) return;

        // Atualiza os dados do gráfico com os dados recebidos
        const categories = data.map((item) => item.name);
        const values = data.map((item) => item.count);

        setChartData((prev) => ({
          ...prev,
          options: {
            ...prev.options,
            xaxis: {
              ...prev.options.xaxis,
              categories: categories,
            },
          },
          series: [
            {
              name: "Atendimentos",
              data: values,
            },
          ],
        }));
      } catch (error) {
        console.error("Erro ao buscar dados do gráfico:", error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [searchDate, originDate]);

  const shouldUpdateTicket = (ticket) => {
    if (!ticket) return false;

    // Verificar consulta por texto
    if (searchParam && searchParam.trim() != "") return false;

    // Verificar se há connectionAndQueuesIds válidos
    if (
      selectedsConnectionsAndQueues &&
      Array.isArray(selectedsConnectionsAndQueues) &&
      selectedsConnectionsAndQueues.length > 0
    ) {
      // identificar conexão e fila
      const connIdx = selectedsConnectionsAndQueues.findIndex(
        (scc) => scc.connectionId == ticket.whatsappId
      );
      if (connIdx == -1) return false;
      if (
        selectedsConnectionsAndQueues[connIdx].queueIds.includes(
          ticket.queueId
        ) == false
      )
        return false;
    }

    // Verificar atribuições de usuários
    if (
      selectedUsers &&
      Array.isArray(selectedUsers) &&
      selectedUsers.length > 0
    ) {
      if (selectedUsers.includes(ticket.userId) == false) return false;
    }

    // Verificar se é para consultar os usuários onlines ou offlines no sistema
    // Não foi feito pq tem que consultar a todo momento a base de usuários online ou não

    // Verificar se é para consultar os atendimentos atribuídos ou não
    if (
      selectedAssigned &&
      Array.isArray(selectedAssigned) &&
      selectedAssigned.length > 0
    ) {
      let isNull = false;
      let notNull = false;

      for (const ass of assigneds) {
        if (ass == "assigned") notNull = true;
        else isNull = true;
      }

      if (isNull == true && notNull == false) {
        // sem atendente atribuído
        if (ticket?.userId && !isNaN(ticket.userId)) return false;
      } else if (isNull == false && notNull == true) {
        // com atendente atribuído
        if (!ticket?.userId && isNaN(ticket.userId)) return false;
      }
    }

    // Verificar status dos atendimentos
    if (
      selectedTicketStatus &&
      Array.isArray(selectedTicketStatus) &&
      selectedTicketStatus.length > 0
    ) {
      if (selectedTicketStatus.includes(ticket.status) == false) return false;
    }

    return true;
  };

  const onSelectConnectionsAndQueues = (opts) => {
    setSelectedsConnectionsAndQueues(
      Array.isArray(opts) && opts.length > 0 ? opts : []
    );
    if (pageNumber > 1) setPageNumber(1);
  };

  const onSelectedUsers = (ids) => {
    setSelectedUsers(ids);
    if (pageNumber > 1) setPageNumber(1);
  };

  const onSelectAssigned = (ids) => {
    setSelectedAssigned(ids);
    if (pageNumber > 1) setPageNumber(1);
  };

  const onSelectTicketStatus = (ids) => {
    setSelectedTicketStatus(ids);
    if (pageNumber > 1) setPageNumber(1);
  };

  const handleDrawer = () => {
    setOpen(!open);
  };

  const handleSelectAll = () => {
    if (selectedTickets.length === tickets.length) {
      unSelectAll();
      return;
    }
    const allTicketIds = tickets.map((ticket) => ticket.id);
    setSelectedTickets(allTicketIds);
  };

  const handleSelect = (ticketId) => {
    if (!ticketId && ticketId != "" && ticketId != "undefined") return;

    setSelectedTickets((prev) =>
      prev.includes(ticketId)
        ? prev.filter((id) => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const unSelectAll = () => {
    setSelectedTickets([]);
  };

  const RenderFilters = () => {
    return (
      <TicketManagementFilters
        selectedsConnectionsAndQueues={selectedsConnectionsAndQueues}
        onSelectConnectionsAndQueues={onSelectConnectionsAndQueues}
        users={users}
        onSelectedUsers={onSelectedUsers}
        selectedUsers={selectedUsers}
        userStatus={userStatus}
        assigneds={assigneds}
        selectedAssigned={selectedAssigned}
        onSelectAssigned={onSelectAssigned}
        ticketStatus={ticketStatus}
        selectedTicketStatus={selectedTicketStatus}
        onSelectTicketStatus={onSelectTicketStatus}
        searchParam={searchParam}
        handleSearchParam={handleSearchParam}
        user={user}
      />
    );
  };

  const handleSearchParam = (txt) => {
    setSearchParam(txt);
    if (pageNumber > 1) setPageNumber(1);
  };

  function resize() {
    if (Number(window.innerWidth) <= 1800) {
      var newValue = {
        filter: "col-xl-4",
        donuts: "col-xl-4",
        table: "col-xl-4",
      };
      setBootstrapResolution(newValue);
    } else {
      setBootstrapResolution(defaultResolution);
    }
    if (Number(window.innerWidth) <= 1200) {
      setHeightChart(heightChartDefault);
    } else {
      setHeightChart("95%");
    }
  }

  window.onresize = resize;
  function openDatePicker(input) {
    var inputDate = document.getElementById(input);
    if (!inputDate.disabled) {
      inputDate.showPicker();
    }
  }

  const filterData = () => {
    var content = { start: dateTime.start, end: dateTime.end, originDate };
    setSearchDate({ start: dateTime.start, end: dateTime.end });
    setValues(content);
  };

  useEffect(() => {
    setTimeout(() => {
      resize();
      setLoading(false);
    }, 1000);
  }, []);

  return (
    <Box className={classes.mainContainer}>
      <Box className={classes.content}>
        {/* Filtros para monitores */}
        <Box
          component="aside"
          className={classNames({
            [classes.baseContent]: true,
            [classes.filterColumn]: true,
          })}
        >
          {/* <Box sx={{ borderRadius: 2, mb: 3, bgcolor: "#fff", boxShadow: 1 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: "100%",
                overflow: "hidden",
                px: 3,
                pt: 3,
                pb: 1,
              }}
            > */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <Box>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                Filtros
              </Typography>
            </Box>
          </Box>

          <Box mt={2}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              Filtrar atendimento(s) por:
            </Typography>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>Data de Finalização</Grid>
              <Grid item>
                <Switch
                  checked={originDate === "createdAt"}
                  onChange={() =>
                    setOriginDate(
                      originDate === "createdAt" ? "finishedAt" : "createdAt"
                    )
                  }
                  color="primary"
                  name="origin_date"
                  id="origin_date"
                />
              </Grid>
              <Grid item>Data de Início</Grid>
            </Grid>
          </Box>

          <Box mt={2}>
            <Box mb={2} onClick={() => openDatePicker("start_date")}>
              <Typography variant="body2" gutterBottom>
                Data de início
              </Typography>
              <input
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) =>
                  setDateTime((prev) => ({
                    ...prev,
                    start: e.target.value,
                  }))
                }
                value={dateTime.start}
                id="start_date"
                type="date"
                className="form-control"
              />
            </Box>
            <Box mb={2} onClick={() => openDatePicker("end_date")}>
              <Typography variant="body2" gutterBottom>
                Data de fim
              </Typography>
              <input
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) =>
                  setDateTime((prev) => ({ ...prev, end: e.target.value }))
                }
                value={dateTime.end}
                id="end_date"
                type="date"
                className="form-control"
              />
            </Box>
            <Button
              variant="contained"
              fullWidth
              className={classes.button}
              onClick={filterData}
            >
              Aplicar Filtro
            </Button>
          </Box>
        </Box>
        {/* </Box> */}
        {/* </Box> */}

        {/* Filtros para mobiles */}
        <Drawer
          anchor="left"
          open={open}
          onClose={handleDrawer}
          classes={{
            paper: classes.filterMobile,
          }}
        >
          {RenderFilters()}
        </Drawer>

        {/* Gráfico */}
        <Box
          component="main"
          className={classNames({
            [classes.baseContent]: true,
            [classes.detailColumn]: true,
          })}
          hidden={
            isSm && (!ticketId || ticketId === "" || ticketId === "undefined")
          }
        >
          <Box
            component="section"
            sx={{
              flex: "1 1 60%",
              p: 2,
              bgcolor: "#fff",
              borderRadius: 2,
              mr: 1,
              boxShadow: 1,
              border: "1px solid #e0e0e0",
            }}
          >
            <Chart
              options={chartData.options}
              series={chartData.series}
              type="bar"
              height={350}
            />
          </Box>
        </Box>

        {/* Botão de filtros mobile */}
        <Tooltip
          title={
            <Typography gutterBottom color="inherit">
              {i18n.t("ticketManagement.filters.title")}
            </Typography>
          }
        >
          <Fab
            color="primary"
            aria-label="add"
            size="small"
            className={classes.filterMobileButton}
            onClick={handleDrawer}
          >
            <FilterListOutlinedIcon />
          </Fab>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default RankingAI;
